/**
 * What the record page reads out (13-25), pure: the head's facts and the read-only sections, in FIELDSET §3's stage
 * order. `components/record/FieldView.tsx` and `pages/Record/RecordPage.tsx` render it.
 *
 * - **Sections:** *The report* (§3.2), *Contact details* (§1.6: phone numbers, emails and names of contacts sit
 *   only in their own section), *Triage and notification* (§3.3), *Investigation and actions* (§3.4), *Closure*
 *   (§3.5). A column sits in the first stage that names it (13-17's `stages`). `Title` and `Status` are the head's;
 *   `Attachments` is the head's link (D-13-L-6); `Created` is the head's lodged date.
 * - **Values** in words (D-13-L-10): days `D MMM YYYY`, instants `D MMM YYYY, h:mm am/pm` Melbourne, by 13-18's
 *   readers. A blank reads "Not recorded", never "No" (§1.1).
 * - **Frozen Person columns** (§1.2: no new writer) are hidden unless they hold a value, then shown "From Centro"
 *   with the site person id the read returns (D-13-07-7: Person columns are read by `…LookupId` only).
 */

import { REGISTER_FIELDS } from '../config/fieldset/registerFields';
import type { FieldSpec } from '../config/fieldset/registerFields';
import { REGISTERS } from '../config/registers';
import type { RecordRegister, RecordRow, Stage } from '../types/records';
import { formatDay } from './fieldValues';
import { CONTACT_COLUMNS } from './lodgeBuilder';
import { isBlank } from './recordVerbs';
import { displayValue } from './verbForm';

/** Each register as a noun, for the head ("Incident", "Improvement"). */
export const RECORD_NOUNS: Readonly<Record<RecordRegister, string>> = {
  incidents: 'Incident',
  complaints: 'Complaint',
  feedback: 'Feedback',
  risk: 'Risk',
  ci: 'Improvement',
};

export type SectionId = 'report' | 'contact' | 'triage' | 'investigation' | 'closure';

export interface FieldItem {
  name: string;
  label: string;
  /** The value in words, or `null` for "Not recorded". */
  value: string | null;
  /** A text area: line breaks kept. */
  multiline: boolean;
  /** A frozen column that holds a Centro value. */
  fromCentro: boolean;
}

export interface FieldSection {
  id: SectionId;
  title: string;
  items: FieldItem[];
}

/** The read section each stage's columns sit in (13.2-11's `openByDefault` opens the next step's). */
export const SECTION_OF_STAGE: Readonly<Record<Stage, SectionId>> = {
  lodge: 'report',
  triage: 'triage',
  notification: 'triage',
  investigation: 'investigation',
  closure: 'closure',
};

const SECTION_TITLES: Readonly<Record<SectionId, string>> = {
  report: 'The report',
  contact: 'Contact details',
  triage: 'Triage and notification',
  investigation: 'Investigation and actions',
  closure: 'Closure',
};

export const SECTION_ORDER: readonly SectionId[] =['report', 'contact', 'triage', 'investigation', 'closure'];

/** Columns the head shows instead of a section. */
function headColumns(register: RecordRegister): ReadonlySet<string> {
  const f = REGISTERS[register].fields;
  return new Set([f.title, f.status, f.created]);
}

function sectionOf(spec: FieldSpec): SectionId {
  if (CONTACT_COLUMNS.has(spec.name)) return 'contact';
  const first = spec.stages[0];
  return first ? SECTION_OF_STAGE[first] : 'report';
}

/** One column's value in words, or `null` when blank. */
export function valueOf(spec: FieldSpec, row: RecordRow): { value: string | null; fromCentro: boolean } {
  if (spec.kind === 'person-frozen') {
    const id = row.fields[`${spec.name}LookupId`] ?? row.fields[spec.name];
    return isBlank(id) ? { value: null, fromCentro: false } : { value: `Site person #${String(id)}`, fromCentro: true };
  }
  const raw = row.fields[spec.name];
  if (isBlank(raw)) return { value: null, fromCentro: false };
  return { value: displayValue(spec, raw), fromCentro: false };
}

/** The read-only sections, in stage order; an empty section is left out. */
export function fieldSections(row: RecordRow): FieldSection[] {
  const register = row.register;
  const skip = headColumns(register);
  const by = new Map<SectionId, FieldItem[]>();
  for (const spec of REGISTER_FIELDS[register]) {
    if (skip.has(spec.name) || spec.kind === 'attachments') continue;
    const { value, fromCentro } = valueOf(spec, row);
    // Frozen columns: hidden unless they hold a value (then "From Centro").
    if (spec.kind === 'person-frozen' && value === null) continue;
    const id = sectionOf(spec);
    const list = by.get(id) ?? [];
    list.push({ name: spec.name, label: spec.label, value, multiline: spec.kind === 'textarea', fromCentro });
    by.set(id, list);
  }
  return SECTION_ORDER.filter((id) => (by.get(id) ?? []).length > 0).map((id) => ({
    id,
    title: SECTION_TITLES[id],
    items: by.get(id) ?? [],
  }));
}

/** When the record was lodged: `Created`, else (a practice record) its earliest sibling row; `D MMM YYYY`. */
export function lodgedOn(row: RecordRow, firstEntryAt?: string | null): string | null {
  const created = row.fields[REGISTERS[row.register].fields.created];
  const value = typeof created === 'string' && created ? created : (firstEntryAt ?? null);
  const label = formatDay(value);
  return label === '—' ? null : label;
}
